import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Eye, Archive, Trash2, Calendar, MoreVertical, RotateCcw, MapPin, Globe } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loading, EmptyState } from "@/components/common/Loading";
import { fullTime } from "@/lib/timeago";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";

const SORTS = [
  { value: "recent", label: "Recently updated" },
  { value: "title_asc", label: "Title A–Z" },
  { value: "title_desc", label: "Title Z–A" },
  { value: "status", label: "Status" },
  { value: "date", label: "Event date" },
];

export default function MyEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [sort, setSort] = useState("recent");
  const [bulkDel, setBulkDel] = useState(false);

  const load = async () => { const { data } = await api.get("/events/mine"); setItems(data); };
  useEffect(() => { load(); }, []);

  // Auto-open the create dialog when arriving from a directory "Create" CTA (?new=1).
  useEffect(() => {
    if (searchParams.get("new")) {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    const t = (x) => (x.title || "").toLowerCase();
    if (sort === "title_asc") arr.sort((a, b) => t(a).localeCompare(t(b)));
    else if (sort === "title_desc") arr.sort((a, b) => t(b).localeCompare(t(a)));
    else if (sort === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || "") || t(a).localeCompare(t(b)));
    else if (sort === "date") arr.sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));
    else arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return arr;
  }, [items, sort]);

  const ids = useMemo(() => sorted.map((i) => i.id), [sorted]);
  const sel = useRowSelection(ids);

  // Groups (GRP-DEC-315): arriving from a Group carries the Group as context — the Event
  // is associated on creation and the canonical editor opens with it selected. `group` is
  // read once here and kept for the create call, so the normal `?new=1` flow is unchanged.
  const fromGroup = React.useRef(searchParams.get("group"));

  const create = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/events", {
        title: name.trim(),
        ...(fromGroup.current ? { group_id: fromGroup.current } : {}),
      });
      setOpen(false); setName("");
      navigate(`/dashboard/events/${data.id}${fromGroup.current
        ? `?group=${fromGroup.current}` : ""}`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create."); }
  };
  const publish = async (it, v) => {
    try { await api.post(`/events/${it.id}/publish?published=${v}`); toast.success(v ? "Published" : "Unpublished"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const archive = async (it, v) => { await api.post(`/events/${it.id}/archive?archived=${v}`); toast.success(v ? "Archived" : "Restored"); load(); };
  const remove = async (it) => { await api.delete(`/events/${it.id}`); toast.success("Deleted"); load(); };

  const bulkPublish = async (v) => { await runBulk(sel.selected, (id) => api.post(`/events/${id}/publish?published=${v}`), v ? "Published" : "Unpublished"); sel.clear(); load(); };
  const bulkRemove = async () => { await runBulk(sel.selected, (id) => api.delete(`/events/${id}`), "Deleted"); sel.clear(); setBulkDel(false); load(); };

  if (!items) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">My Events</h1>
          <p className="mt-1 text-muted-foreground">Create and manage events for the community.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="event-create-button"><Plus className="mr-2 h-4 w-4" /> New event</Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-8"><EmptyState icon={Calendar} title="No events yet" description="Create your first event to gather the community." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Create one</Button>} testId="event-empty" /></div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="mine-select-all" /> Select all
            </label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48" data-testid="mine-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((it) => (
              <Card key={it.id} className={`flex flex-col overflow-hidden ${sel.isSelected(it.id) ? "ring-2 ring-primary" : ""}`} data-testid={`event-card-${it.id}`}>
                <div className="relative h-28 bg-gradient-to-r from-teal-100 to-teal-50">
                  {it.cover_url && <img src={fileUrl(it.cover_url)} alt="" className="h-full w-full object-cover" />}
                  <div className="absolute left-2 top-2 rounded-md bg-card/90 p-1">
                    <Checkbox checked={sel.isSelected(it.id)} onCheckedChange={() => sel.toggle(it.id)} data-testid={`select-event-${it.id}`} />
                  </div>
                  <div className="absolute right-2 top-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="rounded-md bg-card/90 p-1.5 hover:bg-card" data-testid={`event-menu-${it.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/dashboard/events/${it.id}`)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                        {it.published && it.slug && <DropdownMenuItem onClick={() => window.open(`/event/${it.slug}`, "_blank")}><Eye className="mr-2 h-4 w-4" /> View public</DropdownMenuItem>}
                        {it.status === "archived"
                          ? <DropdownMenuItem onClick={() => archive(it, false)}><RotateCcw className="mr-2 h-4 w-4" /> Restore</DropdownMenuItem>
                          : <DropdownMenuItem onClick={() => archive(it, true)}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-[640] text-foreground">{it.title}</h3>
                  {it.start_at && <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Calendar className="h-3.5 w-3.5" /> {fullTime(it.start_at)}</div>}
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    {it.format === "virtual" ? <Globe className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                    {it.format === "virtual" ? "Online" : (it.location?.city || "In person")}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {it.status === "archived" ? <Badge variant="secondary">Archived</Badge> : it.published ? <Badge className="bg-teal-50 text-teal-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                    <Badge variant="secondary" className="capitalize">{it.source}</Badge>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`/dashboard/events/${it.id}`)} data-testid={`event-edit-${it.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                    {it.status !== "archived" && <Button size="sm" variant={it.published ? "ghost" : "default"} onClick={() => publish(it, !it.published)} data-testid={`event-publish-${it.id}`}>{it.published ? "Unpublish" : "Publish"}</Button>}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`event-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {it.title}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(it)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(true)} data-testid="bulk-publish"><Eye className="mr-1.5 h-4 w-4" /> Publish</Button>
        <Button size="sm" variant="secondary" onClick={() => bulkPublish(false)} data-testid="bulk-unpublish">Unpublish</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
      </BulkActionBar>

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {sel.count} event{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="bulk-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New event</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Event title</Label><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Founder Mixer SF" data-testid="event-create-name" /></div>
          <DialogFooter><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="event-create-confirm">Create &amp; edit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
